'use client'

import classNames from 'clsx'
import * as Flags from 'country-flag-icons/react/3x2'
import { useLocale } from 'next-intl'
import type { ComponentType, SVGProps } from 'react'

import AvatarIcon from '@/assets/icons/leaderboard/lb-avatar-icon.svg'
import GlowBronze from '@/assets/icons/leaderboard/lb-glow-bronze.svg'
import GlowGold from '@/assets/icons/leaderboard/lb-glow-gold.svg'
import GlowSelf from '@/assets/icons/leaderboard/lb-glow-me.svg'
import GlowSilver from '@/assets/icons/leaderboard/lb-glow-silver.svg'
import LaurelBronze from '@/assets/icons/leaderboard/lb-laurel-bronze.svg'
import LaurelGold from '@/assets/icons/leaderboard/lb-laurel-gold.svg'
import LaurelSilver from '@/assets/icons/leaderboard/lb-laurel-silver.svg'
import Image from '@/ui/MediaImage'
import { countryNameToCode } from '@/utils/countryNameToCode'
import { isSvgImageSrc, shouldUnoptimizeImageSrc } from '@/utils/imageUrl'
import { regionNameFromCode } from '@/utils/regionNameFromCode'

import S from './LeaderboardRow.module.scss'

export type RowTone = 'gold' | 'silver' | 'bronze' | 'self' | 'default'

const MEDALS: RowTone[] = ['gold', 'silver', 'bronze']

/** Places 1–3 keep their medal look even for the viewer's own row. */
export function rowTone(rank: number, isSelf: boolean): RowTone {
  return MEDALS[rank - 1] ?? (isSelf ? 'self' : 'default')
}

export const toneRowClass: Record<RowTone, string | undefined> = {
  gold: S.gold,
  silver: S.silver,
  bronze: S.bronze,
  self: S.self,
  default: undefined
}

const toneRankClass: Record<RowTone, string | undefined> = {
  gold: S.rankGold,
  silver: S.rankSilver,
  bronze: S.rankBronze,
  self: S.rankSelf,
  default: undefined
}

type SvgComponent = ComponentType<SVGProps<SVGSVGElement>>

const GLOWS: Partial<Record<RowTone, SvgComponent>> = {
  gold: GlowGold,
  silver: GlowSilver,
  bronze: GlowBronze,
  self: GlowSelf
}

const LAURELS: Partial<Record<RowTone, SvgComponent>> = {
  gold: LaurelGold,
  silver: LaurelSilver,
  bronze: LaurelBronze
}

export function RowGlow({ tone }: { tone: RowTone }) {
  const Glow = GLOWS[tone]
  if (!Glow) {
    return null
  }
  return (
    <span className={S.glow} aria-hidden>
      <span className={S.glowInner}>
        <Glow />
      </span>
    </span>
  )
}

export function RankCell({ rank, tone }: { rank: number; tone: RowTone }) {
  const Laurel = LAURELS[tone]
  return (
    <div className={S.colRank}>
      <span className={classNames(S.rank, toneRankClass[tone])}>{rank}</span>
      {Laurel ? <Laurel className={S.laurel} aria-hidden /> : null}
    </div>
  )
}

export function FanAvatar({
  src,
  alt,
  compact = false
}: {
  src?: string
  alt: string
  compact?: boolean
}) {
  return (
    <span className={classNames(S.avatar, compact && S.avatarCompact)}>
      {src ? (
        <Image
          src={src}
          alt={alt}
          width={36}
          height={36}
          className={S.avatarImage}
          unoptimized={shouldUnoptimizeImageSrc(src)}
        />
      ) : (
        <AvatarIcon className={S.avatarPlaceholder} aria-hidden />
      )}
    </span>
  )
}

export function SportIcon({ src }: { src?: string | null }) {
  if (!src) {
    return null
  }
  return (
    <Image
      src={src}
      alt=""
      width={14}
      height={14}
      className={S.sportIcon}
      unoptimized={isSvgImageSrc(src) || shouldUnoptimizeImageSrc(src)}
    />
  )
}

type FlagComponent = ComponentType<SVGProps<SVGSVGElement> & { title?: string }>
const FLAG_COMPONENTS = Flags as unknown as Record<string, FlagComponent>

/** Profile countries are free text (a name or an ISO code). */
export function useCountry(raw?: string | null) {
  const locale = useLocale()
  const code = countryNameToCode(raw)
  const name = code ? regionNameFromCode(locale, code) : (raw ?? '').trim()
  return { code, name }
}

export function CountryFlag({ code }: { code: string }) {
  const Flag = code ? FLAG_COMPONENTS[code] : undefined
  return Flag ? <Flag className={S.flag} title={code} /> : null
}
